import React, {useCallback, useEffect, useRef, useState} from 'react';
import {FiPlay, FiRefreshCw} from 'react-icons/fi';
import {motionReviewApi, readableError} from './api';
import './motionHighlights.css';

export function highlightAge(value, now = Date.now()) {
  if (!value || !Number.isFinite(new Date(value).getTime())) return 'Tag time unavailable';
  const minutes = Math.max(0, Math.floor((now - new Date(value).getTime()) / 60000));
  if (minutes < 1) return 'Tagged just now';
  if (minutes < 60) return `Tagged ${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Tagged ${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? 'Tagged yesterday' : `Tagged ${days} days ago`;
}

function HighlightClip({track, now}) {
  const element = useRef(null);
  const video = useRef(null);
  const [visible, setVisible] = useState(false);
  const [inView, setInView] = useState(false);
  const [pageVisible, setPageVisible] = useState(() => !document.hidden);
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!window.IntersectionObserver) { setVisible(true); setInView(true); return undefined; }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setVisible(true);
      setInView(entry.isIntersecting);
    }, {threshold: 0.01});
    observer.observe(element.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    const change = () => setPageVisible(!document.hidden);
    document.addEventListener('visibilitychange', change);
    return () => document.removeEventListener('visibilitychange', change);
  }, []);
  useEffect(() => {
    const player = video.current;
    if (!player) return undefined;
    let active = true;
    player.defaultPlaybackRate = 2.5;
    player.playbackRate = 2.5;
    if (inView && pageVisible) {
      player.muted = true;
      player.play()?.catch(() => { if (active) setPlaying(false); });
    } else player.pause();
    return () => { active = false; player.pause(); };
  }, [inView, pageVisible, visible, track.videoUrl]);
  useEffect(() => { setFailed(false); setPlaying(false); }, [track.videoUrl]);
  const names = track.taggedBy || ['Reviewer'];
  return <article className="cr-highlight" ref={element}>
    <div className="cr-highlight-media">
      {visible && <video ref={video} src={`${track.videoUrl}#t=0.001`} preload="metadata" playsInline muted loop autoPlay={inView && pageVisible} controls={false}
        aria-label={`Motion track ${highlightAge(track.taggedAt, now).toLowerCase()}`}
        onLoadedMetadata={(event) => { event.currentTarget.defaultPlaybackRate = 2.5; event.currentTarget.playbackRate = 2.5; }}
        onError={() => setFailed(true)} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} />}
      {!playing && !failed && <button className="cr-highlight-play" aria-label="Play highlighted motion track" onClick={() => {
        setVisible(true);
        if (video.current) video.current.play()?.catch(() => setPlaying(false));
      }}><FiPlay aria-hidden="true" /></button>}
      {failed && <p className="cr-highlight-unavailable">This clip couldn’t load.<br />Refresh highlights to try again.</p>}
    </div>
    <div className="cr-highlight-meta">
      <time dateTime={track.taggedAt || undefined} title={track.taggedAt ? new Date(track.taggedAt).toLocaleString() : undefined}>{highlightAge(track.taggedAt, now)}</time>
      <span title={names.join(', ')}>Tagged by <strong>{names[0] || 'Reviewer'}</strong>{names.length > 1 && <details><summary>+ {names.length - 1} {names.length === 2 ? 'other' : 'others'}</summary><span>{names.slice(1).join(', ')}</span></details>}</span>
    </div>
  </article>;
}

export default function MotionHighlights({api = motionReviewApi, initialHighlightId = null}) {
  const [tracks, setTracks] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');
  const [linkedNotice, setLinkedNotice] = useState('');
  const [now, setNow] = useState(Date.now);
  const request = useRef(0);
  const load = useCallback(async (after = null) => {
    const id = ++request.current;
    setBusy(true); setError('');
    try {
      let result; let next = after; const incoming = [];
      if (!after && initialHighlightId) {
        const linked = await api.highlights({highlightId: initialHighlightId});
        if (id !== request.current) return;
        incoming.push(...linked.tracks);
        setLinkedNotice(linked.tracks.length ? 'The sighting from your email appears first.' : 'That sighting is no longer available. Here are the latest highlights.');
      } else if (!after) setLinkedNotice('');
      // Skip a few pages of removed/unavailable evidence without scanning history.
      for (let page = 0; page < 4; page += 1) {
        result = await api.highlights(next ? {cursor: next} : {});
        if (id !== request.current) return;
        incoming.push(...result.tracks); next = result.nextCursor;
        if (incoming.length || !next) break;
      }
      setTracks(previous => [...new Map([...(after ? previous : []), ...incoming].map(track => [track.id, track])).values()]);
      setCursor(next);
    } catch (failure) { if (id === request.current) setError(readableError(failure)); }
    finally { if (id === request.current) setBusy(false); }
  }, [api, initialHighlightId]);
  useEffect(() => { load(); return () => { request.current += 1; }; }, [load]);
  useEffect(() => {
    if (!tracks.length) return undefined;
    let active = true;
    let checking = false;
    const check = async () => {
      if (document.hidden || checking) return;
      checking = true;
      try {
        const removed = new Set();
        for (let offset = 0; offset < tracks.length; offset += 100) {
          const ids = tracks.slice(offset, offset + 100).map(track => track.id);
          const result = await api.highlights({checkIds: ids});
          if (!active) return;
          if (!Array.isArray(result.activeIds)) return;
          const available = new Set(result.activeIds);
          ids.forEach(id => { if (!available.has(id)) removed.add(id); });
        }
        if (active && removed.size) setTracks(previous => previous.filter(track => !removed.has(track.id)));
      } catch (_) {
        // A failed connectivity check is not evidence that tracks were removed.
      } finally { checking = false; }
    };
    const timer = setInterval(check, 30000);
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', check);
    return () => {
      active = false;
      clearInterval(timer);
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', check);
    };
  }, [api, tracks]);
  useEffect(() => { const timer = setInterval(() => setNow(Date.now()), 60000); return () => clearInterval(timer); }, []);
  return <main className="cr-main cr-highlights">
    <div className="cr-highlights-heading"><div><h1>Highlights</h1><p>Recent tracks highlighted by the community</p></div>
      <div className="cr-highlights-tools"><span>Most recently tagged</span><button type="button" aria-label="Refresh highlights" disabled={busy} onClick={() => load()}><FiRefreshCw aria-hidden="true" /></button></div>
    </div>
    {error && <p className="cr-error" role="alert">{error} <button className="cr-text-button" onClick={() => load(cursor)}>Try again</button></p>}
    {linkedNotice && <p className="cr-announcement" role="status">{linkedNotice}</p>}
    <div className="cr-highlights-grid">{tracks.map(track => <HighlightClip key={track.id} track={track} now={now} />)}</div>
    {busy && <p className="cr-highlights-status" role="status">Loading highlights…</p>}
    {!busy && !error && !tracks.length && <p className="cr-highlights-status">No highlights to show yet. Community highlights will appear here.</p>}
    {!busy && cursor && <button className="cr-primary cr-highlights-more" onClick={() => load(cursor)}>Load more</button>}
  </main>;
}
